import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Eye,
  Check,
  Plus,
  Trash2,
  HelpCircle,
  Package,
  RotateCcw,
  CreditCard,
  Users,
  Shield,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import axios from 'axios';
import Base from '../Base';

const FAQsForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    hero: {
      title: 'Frequently Asked Questions',
      subtitle: ''
    },
    categories: []
  });

  const iconOptions = [
    { value: 'Package', label: 'Package', Icon: Package },
    { value: 'RotateCcw', label: 'Returns', Icon: RotateCcw },
    { value: 'CreditCard', label: 'Payment', Icon: CreditCard },
    { value: 'Users', label: 'Account', Icon: Users },
    { value: 'Shield', label: 'Security', Icon: Shield },
    { value: 'MessageCircle', label: 'Support', Icon: MessageCircle },
    { value: 'HelpCircle', label: 'General', Icon: HelpCircle }
  ];

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/template/${templateId}/page/faqs`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });

      console.log('FAQs page response:', response.data);

      // Handle different response structures
      let content = null;
      if (response.data.content) {
        content = response.data.content;
      } else if (response.data.data?.content) {
        content = response.data.data.content;
      }

      if (content) {
        setFormData({
          hero: {
            title: content.hero?.title || 'Frequently Asked Questions',
            subtitle: content.hero?.subtitle || ''
          },
          categories: content.categories || []
        });
      }
    } catch (error) {
      console.error('Error loading FAQs page data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  const handleSave = async (isDraft = true) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      await axios.post(`/api/template/${templateId}/page/faqs/save`, {
        status: isDraft ? 'draft' : 'complete',
        content: formData
      }, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      setLastSaved(new Date());
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving:', error);
      setErrorMessage(error.response?.data?.message || 'Error saving changes. Please try again.');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    console.log('Preview:', formData);
  };

  // Success Modal Component - Vasstr colors
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your FAQs have been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component - Simple design
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  const addCategory = () => {
    setFormData(prev => ({
      ...prev,
      categories: [
        ...prev.categories,
        { name: '', icon: 'HelpCircle', questions: [] }
      ]
    }));
  };

  const updateCategory = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, i) =>
        i === index ? { ...cat, [field]: value } : cat
      )
    }));
  };

  const removeCategory = (index) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index)
    }));
  };

  const addQuestion = (categoryIndex) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, i) =>
        i === categoryIndex
          ? { ...cat, questions: [...cat.questions, { question: '', answer: '' }] }
          : cat
      )
    }));
  };

  const updateQuestion = (categoryIndex, questionIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, i) =>
        i === categoryIndex
          ? {
              ...cat,
              questions: cat.questions.map((q, j) =>
                j === questionIndex ? { ...q, [field]: value } : q
              )
            }
          : cat
      )
    }));
  };

  const removeQuestion = (categoryIndex, questionIndex) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, i) =>
        i === categoryIndex
          ? { ...cat, questions: cat.questions.filter((_, j) => j !== questionIndex) }
          : cat
      )
    }));
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading FAQs...</p>
        </div>
      </div>
    } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/template-builder/${templateId}`)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">FAQs</h1>
                {lastSaved && (
                  <p className="text-xs text-gray-500">
                    Last saved {lastSaved.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handlePreview}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Preview</span>
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-[#D45614] text-white rounded-lg hover:bg-[#D45614]-dark transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Save & Complete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Hero Section */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#D45614]-light rounded-lg flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Hero Section</h2>
                <p className="text-sm text-gray-600">The header section of your FAQs page</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.hero.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, hero: { ...prev.hero, title: e.target.value } }))}
                  placeholder="Frequently Asked Questions"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtitle (Optional)
                </label>
                <input
                  type="text"
                  value={formData.hero.subtitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, hero: { ...prev.hero, subtitle: e.target.value } }))}
                  placeholder="Find answers to common questions"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">FAQ Categories</h2>
                <p className="text-sm text-gray-600">Organize your FAQs by category</p>
              </div>
              <button
                onClick={addCategory}
                className="flex items-center gap-2 px-4 py-2 bg-[#D45614]-light text-primary rounded-lg hover:bg-[#D45614] hover:text-white transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Category
              </button>
            </div>

            {formData.categories.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg">
                <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">No categories added yet</p>
                <button
                  onClick={addCategory}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#D45614] text-white rounded-lg hover:bg-[#D45614]-dark transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add First Category
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {formData.categories.map((category, catIndex) => (
                  <div key={catIndex} className="p-6 bg-white rounded-lg border-2 border-gray-100">
                    {/* Category Header */}
                    <div className="space-y-4 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">
                          Category {catIndex + 1}
                        </span>
                        <button
                          onClick={() => removeCategory(catIndex)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Delete category"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category Name *
                          </label>
                          <input
                            type="text"
                            value={category.name}
                            onChange={(e) => updateCategory(catIndex, 'name', e.target.value)}
                            placeholder="Orders & Shipping"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Icon
                          </label>
                          <select
                            value={category.icon}
                            onChange={(e) => updateCategory(catIndex, 'icon', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          >
                            {iconOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Questions */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">Questions</h3>
                        <button
                          onClick={() => addQuestion(catIndex)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-[#D45614]-light text-primary text-sm rounded-lg hover:bg-[#D45614] hover:text-white transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                          Add Question
                        </button>
                      </div>

                      {category.questions.length === 0 ? (
                        <div className="text-center py-8 bg-white rounded-lg">
                          <p className="text-gray-500 text-sm mb-3">No questions in this category</p>
                          <button
                            onClick={() => addQuestion(catIndex)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D45614] text-white text-sm rounded-lg hover:bg-[#D45614]-dark transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                            Add Question
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {category.questions.map((question, qIndex) => (
                            <div key={qIndex} className="p-4 bg-white rounded-lg border space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-600">
                                  Q{qIndex + 1}
                                </span>
                                <button
                                  onClick={() => removeQuestion(catIndex, qIndex)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  title="Delete question"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Question *
                                </label>
                                <input
                                  type="text"
                                  value={question.question}
                                  onChange={(e) => updateQuestion(catIndex, qIndex, 'question', e.target.value)}
                                  placeholder="How long does shipping take?"
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Answer *
                                </label>
                                <textarea
                                  value={question.answer}
                                  onChange={(e) => updateQuestion(catIndex, qIndex, 'answer', e.target.value)}
                                  placeholder="Standard shipping takes 5-7 business days. Express shipping is available for 2-3 day delivery."
                                  rows={3}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Add Common Categories */}
          {formData.categories.length === 0 && (
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
              <h3 className="font-semibold text-blue-900 mb-3">Quick Add Common Categories</h3>
              <p className="text-sm text-blue-700 mb-4">
                Add commonly used FAQ categories with one click:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { name: 'Orders & Shipping', icon: 'Package' },
                  { name: 'Returns & Exchanges', icon: 'RotateCcw' },
                  { name: 'Payment & Pricing', icon: 'CreditCard' },
                  { name: 'Account & Login', icon: 'Users' },
                  { name: 'Privacy & Security', icon: 'Shield' },
                  { name: 'Customer Support', icon: 'MessageCircle' }
                ].map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        categories: [...prev.categories, { name: cat.name, icon: cat.icon, questions: [] }]
                      }));
                    }}
                    className="px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-100 transition-colors text-sm text-left"
                  >
                    + {cat.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tips */}
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
            <h3 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              FAQs Tips
            </h3>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Group related questions into categories</li>
              <li>• Keep questions short and specific</li>
              <li>• Provide clear, helpful answers</li>
              <li>• Include the most common questions first</li>
              <li>• Update regularly based on customer inquiries</li>
              <li>• Add a contact option for unanswered questions</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default FAQsForm;
